// ClaudeCode: API client — all calls to the Vault backend

const BASE = '/v1';

function getToken(): string | null {
  return sessionStorage.getItem('vault_token');
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Auth ──

export interface LoginResponse {
  token: string;
  user: { id: string; email: string; displayName: string };
  cryptoSalt: string;
  hasPassphrase: boolean;
  encryptedVerification: string | null;
}

export async function login(
  email: string,
  password: string
): Promise<LoginResponse> {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function register(
  email: string,
  password: string,
  displayName: string
): Promise<LoginResponse> {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, displayName }),
  });
}

export async function ssoExchange(
  trustToken: string
): Promise<LoginResponse> {
  return request('/auth/sso', {
    method: 'POST',
    body: JSON.stringify({ trust_token: trustToken }),
  });
}

export async function setPassphrase(
  encryptedVerification: string
): Promise<{ ok: boolean }> {
  return request('/auth/set-passphrase', {
    method: 'POST',
    body: JSON.stringify({ encryptedVerification }),
  });
}

// ── Entries ──

export interface EntryMeta {
  id: string;
  title: string;
  tier: number;
  category: string | null;
  url: string | null;
  favorite: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface EntryFull extends EntryMeta {
  encryptedValue: string;
  encryptedNotes: string | null;
  userId: string;
  tenantId: string;
}

export async function listEntries(params?: {
  limit?: number;
  offset?: number;
  tier?: number;
  category?: string;
  favorite?: boolean;
}): Promise<{ entries: EntryMeta[]; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  if (params?.tier) qs.set('tier', String(params.tier));
  if (params?.category) qs.set('category', params.category);
  if (params?.favorite) qs.set('favorite', 'true');
  return request(`/entries?${qs}`);
}

export async function searchEntries(
  q: string
): Promise<{ entries: EntryMeta[]; query: string }> {
  return request(`/entries/search?q=${encodeURIComponent(q)}`);
}

export async function getEntry(id: string): Promise<EntryFull> {
  return request(`/entries/${id}`);
}

export async function createEntry(data: {
  title: string;
  encryptedValue: string;
  tier?: number;
  category?: string;
  encryptedNotes?: string;
  url?: string;
  tags?: string[];
}): Promise<{ id: string }> {
  return request('/entries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEntry(
  id: string,
  data: Partial<{
    title: string;
    encryptedValue: string;
    tier: number;
    category: string;
    encryptedNotes: string;
    url: string;
    favorite: boolean;
    tags: string[];
  }>
): Promise<EntryMeta> {
  return request(`/entries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEntry(id: string): Promise<void> {
  return request(`/entries/${id}`, { method: 'DELETE' });
}

// ── Share links ──

export async function createShareLink(data: {
  encryptedPayload: string;
  expiresInHours?: number;
  maxViews?: number;
  label?: string;
}): Promise<{ id: string; expiresAt: string; maxViews: number }> {
  return request('/share', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function redeemShareLink(
  id: string
): Promise<{ encryptedPayload: string }> {
  return request(`/share/${id}`);
}

export async function listShareLinks(): Promise<{
  links: Array<{
    id: string;
    label: string | null;
    expiresAt: string;
    maxViews: number;
    viewCount: number;
    active: boolean;
    createdAt: string;
  }>;
}> {
  return request('/share');
}

export async function revokeShareLink(id: string): Promise<void> {
  return request(`/share/${id}`, { method: 'DELETE' });
}
